import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useImageCache from '../../hooks/useImageCache';
import { MEDIA_VARIANTS } from '../../media';
import AvatarComposite from '../Avatar/AvatarComposite';
import { fetchAvatarImage } from '../Avatar/AvatarAPI';
import AvatarTintLayer, {
  AVATAR_TINT_MASK_RENDER_MODE,
  resolveAvatarTintUrls,
} from '../Avatar/AvatarTintLayer';
import {
  AVATAR_PROFILES,
  fallbackSelection,
  getEnabledPaletteSwatches,
  randomizeSelection,
} from '../Avatar/avatarModel';
import { usesLastCleanShirtOcclusion } from '../Avatar/avatarOcclusion';
import AdminAPI, {
  getAvatarAdminConfig,
  publishAvatarPart,
  reactivateAvatarPart,
  reopenAvatarPart,
  retireAvatarPart,
  saveAvatarLayerRequirement,
  saveAvatarPart,
} from './AdminAPI';
import {
  ART_DIRECTION_REFERENCE,
  BASE_BODY_REFERENCE,
  buildAvatarPartPrompt,
  PROPORTION_GUIDE,
  PROMPT_GEOMETRY_STATUS,
  PROMPT_TEMPLATE_KEY,
  REGISTRATION_OVERLAY,
} from './avatarPartPrompt';
import './Admin_Operatives.css';

const MEDIA_PROFILE = 'progresslands.avatar.v1';
const RGBA_RENDER_MODE = 'rgba';
const PREVIEW_PARTS_COOKIE = 'progresslands_admin_avatar_preview_v1';
const PREVIEW_PARTS_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const CATEGORY_DEFAULT_SLOTS = {
  category_backgrounds: 'background',
  category_face: 'body_base',
  category_eyes: 'eyes',
  category_hair: 'hair_front',
  category_clothes: 'clothes_base',
  category_extras: 'eyewear',
};
const BULK_PART_ACTIONS = {
  publish: {
    label: 'Publish drafts',
    sourceState: 'draft',
    run: publishAvatarPart,
    successVerb: 'Published',
  },
  reopen: {
    label: 'Reopen as draft',
    sourceState: ['published', 'retired'],
    run: reopenAvatarPart,
    successVerb: 'Reopened',
  },
  retire: {
    label: 'Retire published',
    sourceState: 'published',
    run: retireAvatarPart,
    successVerb: 'Retired',
  },
  reactivate: {
    label: 'Reactivate retired',
    sourceState: 'retired',
    run: reactivateAvatarPart,
    successVerb: 'Reactivated',
  },
};

function canBulkChangePart(part, action) {
  const definition = BULK_PART_ACTIONS[action];
  if (!definition) return false;
  const allowedStates = Array.isArray(definition.sourceState)
    ? definition.sourceState
    : [definition.sourceState];
  return allowedStates.includes(part.state)
    && !(part.builtInFallback && (action === 'reopen' || action === 'retire'));
}

function readPreviewPartIds() {
  if (typeof document === 'undefined') return {};
  const prefix = `${PREVIEW_PARTS_COOKIE}=`;
  const cookie = document.cookie.split('; ').find(value => value.startsWith(prefix));
  if (!cookie) return {};

  try {
    const parsed = JSON.parse(decodeURIComponent(cookie.slice(prefix.length)));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed)
      .filter(([slotKey, partId]) => slotKey && typeof partId === 'string' && partId));
  } catch {
    document.cookie = `${PREVIEW_PARTS_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
    return {};
  }
}

function writePreviewPartIds(partsBySlot) {
  if (typeof document === 'undefined') return;
  const idsBySlot = Object.fromEntries(Object.entries(partsBySlot)
    .filter(([, part]) => part?.id && part.id !== '__preview_unsaved')
    .map(([slotKey, part]) => [slotKey, part.id]));
  if (!Object.keys(idsBySlot).length) {
    document.cookie = `${PREVIEW_PARTS_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
    return;
  }
  document.cookie = `${PREVIEW_PARTS_COOKIE}=${encodeURIComponent(JSON.stringify(idsBySlot))}; Max-Age=${PREVIEW_PARTS_COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
}

function previewPartsFromCookie(config) {
  const partsById = new Map((config?.parts || []).map(part => [part.id, part]));
  return Object.fromEntries(Object.entries(readPreviewPartIds()).flatMap(([slotKey, partId]) => {
    const part = partsById.get(partId);
    return part?.slotKey === slotKey ? [[slotKey, part]] : [];
  }));
}

function list(value) {
  return String(value || '').split(',').map(item => item.trim()).filter(Boolean);
}

function getPublishedPalettes(config) {
  return [...(config?.palettes || [])]
    .filter(palette => palette.state === 'published')
    .sort((left, right) => (
      String(left.name || '').localeCompare(String(right.name || ''))
      || String(left.id || '').localeCompare(String(right.id || ''))
    ));
}

function UploadedLayerPreview({ config, part }) {
  const fetchImage = useCallback((mediaId, variant) => fetchAvatarImage(mediaId, variant), []);
  const getImageUrl = useImageCache(fetchImage);
  const mediaId = part.assets?.mediaId;
  const tintMask = part.renderMode === AVATAR_TINT_MASK_RENDER_MODE;
  const url = tintMask ? null : getImageUrl(mediaId, MEDIA_VARIANTS.AVATAR);
  const tintUrls = tintMask
    ? resolveAvatarTintUrls(part, MEDIA_VARIANTS.AVATAR, getImageUrl)
    : { inkUrl: null, maskUrl: null };
  const selectedDefault = tintMask
    ? getEnabledPaletteSwatches(part, config)
      .find(swatch => swatch.id === part.defaultSwatchId)
    : null;
  const tintPreviewReady = tintUrls.inkUrl && tintUrls.maskUrl && selectedDefault;
  const tintStatus = !mediaId || !part.assets?.tintMaskMediaId
    ? 'Upload both the fixed overlay and tint mask to preview this part.'
    : !selectedDefault
      ? 'Choose a published palette and enabled default swatch to preview this tint-mask part.'
      : 'Loading uploaded tint-mask pair…';

  return (
    <section aria-label="Uploaded layer preview" className="admin-operatives__layer-preview">
      <h4>Uploaded Layer</h4>
      {tintMask && tintPreviewReady
        ? <div
            aria-label={`${part.name || 'Avatar part'} tint-mask layer using ${selectedDefault.label || selectedDefault.id}`}
            className="admin-operatives__layer-preview-artwork"
            role="img"
          >
            <AvatarTintLayer
              dataSlot={part.slotKey}
              inkUrl={tintUrls.inkUrl}
              maskUrl={tintUrls.maskUrl}
              swatch={selectedDefault}
            />
          </div>
        : tintMask
          ? <p>{tintStatus}</p>
        : url
        ? <img
            alt={`${part.name || 'Avatar part'} layer by itself`}
            className={usesLastCleanShirtOcclusion(part) ? 'avatar-composite__layer--last-clean-shirt' : undefined}
            decoding="async"
            draggable="false"
            src={url}
          />
        : <p>{mediaId ? 'Loading uploaded layer…' : 'No uploaded layer yet.'}</p>}
    </section>
  );
}

function TutorialModal({ onClose }) {
  const dialogRef = useRef(null);
  const previousFocus = useRef(document.activeElement);

  useEffect(() => {
    const restoreFocus = previousFocus.current;
    dialogRef.current?.querySelector('button')?.focus();
    return () => restoreFocus?.focus?.();
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'Tab') {
      event.preventDefault();
      dialogRef.current?.querySelector('button')?.focus();
    }
  };

  return (
    <div
      className="admin-operatives__tutorial-backdrop"
      onKeyDown={handleKeyDown}
      onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}
    >
      <section
        aria-labelledby="avatar-v3-tutorial-title"
        aria-modal="true"
        className="admin-operatives__tutorial"
        ref={dialogRef}
        role="dialog"
      >
        <header className="admin-operatives__tutorial-header">
          <h3 id="avatar-v3-tutorial-title">Create frontal Avatar V3 parts in 5 steps</h3>
          <button aria-label="Close tutorial" onClick={onClose} type="button">×</button>
        </header>
        <div className="admin-operatives__tutorial-content">
          <ol>
            <li>Use the approved <strong>BaseBodyV3</strong> as the exact pixel authority for every fitted layer.</li>
            <li>Attach BaseBodyV3 first, the calibrated registration overlay second, the frontal guide third, and the art-direction concept fourth. Jacket, front gear, and iris prompts name one required dependent layer as Image 5.</li>
            <li>Keep its measured 31% head, 60% torso/legs, and 9% feet proportions. No perspective, foreshortening, or unequal paired geometry.</li>
            <li>Generate one isolated slot layer, fit it on the native canvas, then preview it with its dependent layers at full and map size.</li>
            <li>Upload the validated V3 layer as a draft and publish it only after its seams and alpha pass inspection.</li>
          </ol>
          <p className="admin-operatives__references">
            V3 guide folder: <code>_docs/Progress_Lands_Avatar_System_v1.0/assets/avatar-v3/</code>
            {' '}(<code>{PROPORTION_GUIDE}</code>, <code>{REGISTRATION_OVERLAY}</code>; approved <code>{BASE_BODY_REFERENCE}</code>)
            {' '}. Base generation uses <code>{PROPORTION_GUIDE}</code> for geometry and <code>{ART_DIRECTION_REFERENCE}</code> for rendering style.
          </p>
          <p className="admin-operatives__warning"><strong>V3 runtime:</strong> {PROMPT_TEMPLATE_KEY} is {PROMPT_GEOMETRY_STATUS.replace(/_/g, ' ')}. BaseBodyV3 is approved, measured, hashed, calibrated, and active for previews, uploads, and player rendering.</p>
        </div>
      </section>
    </div>
  );
}

function draftFor(config, part = null) {
  return part ? {
    ...part,
    assets: { ...(part.assets || {}) },
    renderMode: part.renderMode || RGBA_RENDER_MODE,
    paletteId: part.paletteId || '',
    defaultSwatchId: part.defaultSwatchId || '',
    flavorText: part.flavorText || '',
    starterEligible: !!part.starterEligible,
    randomWeight: part.randomWeight ?? 1,
    sortOrder: part.sortOrder ?? 0,
    tagsText: (part.tags || []).join(', '),
    requiresTagsText: (part.requiresTags || []).join(', '),
    excludesTagsText: (part.excludesTags || []).join(', '),
    hidesSlotsText: (part.hidesSlots || []).join(', '),
  } : {
    id: '',
    name: '',
    slug: '',
    flavorText: '',
    templateKey: config?.templateKey || 'full_body_front_v1',
    categoryId: config?.categories?.[0]?.id || '',
    slotKey: config?.layerSlots?.find(slot => slot.key === 'body_base')?.key
      || config?.layerSlots?.[0]?.key
      || 'body_base',
    renderMode: RGBA_RENDER_MODE,
    assets: {},
    starterEligible: true,
    randomWeight: 1,
    sortOrder: 0,
    state: 'draft',
    tagsText: '',
    requiresTagsText: '',
    excludesTagsText: '',
    hidesSlotsText: '',
    visibilityProfiles: ['portrait', 'map-selected', 'map-normal'],
  };
}

export default function AdminOperatives() {
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [layerFilter, setLayerFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkMessage, setBulkMessage] = useState('');
  const [showOnlyPreviewed, setShowOnlyPreviewed] = useState(false);
  const [previewPartsBySlot, setPreviewPartsBySlot] = useState({});
  const [previewMode, setPreviewMode] = useState('full');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const previewHydrated = useRef(false);
  const bulkSelectAllRef = useRef(null);

  const load = useCallback(async (preferredId = '') => {
    const nextConfig = await getAvatarAdminConfig();
    setConfig(nextConfig);
    if (!previewHydrated.current) {
      setPreviewPartsBySlot(previewPartsFromCookie(nextConfig));
      previewHydrated.current = true;
    }
    const availablePartIds = new Set((nextConfig.parts || []).map(part => part.id));
    setBulkSelectedIds(current => new Set([...current].filter(id => availablePartIds.has(id))));
    const selected = nextConfig.parts?.find(part => part.id === (preferredId || selectedId)) || nextConfig.parts?.[0];
    setSelectedId(selected?.id || '');
    setDraft(selected ? draftFor(nextConfig, selected) : draftFor(nextConfig));
  }, [selectedId]);

  useEffect(() => { load().catch(() => setMessage('Could not load the Operative Workshop.')); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (config && previewHydrated.current) writePreviewPartIds(previewPartsBySlot);
  }, [config, previewPartsBySlot]);

  const update = (field, value) => setDraft(current => ({ ...current, [field]: value }));
  const updateCategory = (categoryId) => setDraft(current => ({
    ...current,
    categoryId,
    slotKey: current.id ? current.slotKey : (CATEGORY_DEFAULT_SLOTS[categoryId] || current.slotKey),
  }));
  const payload = () => {
    const next = {
      ...draft,
      assets: { ...(draft.assets || {}) },
      tags: list(draft.tagsText),
      requiresTags: list(draft.requiresTagsText),
      excludesTags: list(draft.excludesTagsText),
      hidesSlots: list(draft.hidesSlotsText),
    };
    if (next.renderMode === RGBA_RENDER_MODE) {
      delete next.assets.tintMaskMediaId;
      delete next.paletteId;
      delete next.defaultSwatchId;
    }
    return next;
  };

  const changeRenderMode = (renderMode) => setDraft(current => {
    if (renderMode === RGBA_RENDER_MODE) {
      const assets = { ...(current.assets || {}) };
      delete assets.tintMaskMediaId;
      return {
        ...current,
        assets,
        defaultSwatchId: '',
        paletteId: '',
        renderMode,
      };
    }

    const palettes = getPublishedPalettes(config);
    const paletteId = palettes.some(palette => palette.id === current.paletteId)
      ? current.paletteId
      : palettes[0]?.id || '';
    const next = { ...current, paletteId, renderMode };
    const swatches = getEnabledPaletteSwatches(next, config);
    return {
      ...next,
      defaultSwatchId: swatches.some(swatch => swatch.id === current.defaultSwatchId)
        ? current.defaultSwatchId
        : swatches[0]?.id || '',
    };
  });

  const changePalette = (paletteId) => setDraft(current => {
    const next = { ...current, paletteId };
    const swatches = getEnabledPaletteSwatches(next, config);
    return {
      ...next,
      defaultSwatchId: swatches.some(swatch => swatch.id === current.defaultSwatchId)
        ? current.defaultSwatchId
        : swatches[0]?.id || '',
    };
  });

  const save = async () => {
    setBusy(true);
    setMessage('');
    try {
      const saved = await saveAvatarPart(payload());
      await load(saved.id);
      setMessage('Draft saved.');
    } catch (error) {
      setMessage('Could not save this part. Check its name, slug, category, and slot.');
    } finally {
      setBusy(false);
    }
  };

  const uploadAsset = async (event, assetKey) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (draft.state !== 'draft') {
      setMessage('Reopen this part as a draft before replacing its artwork.');
      return;
    }
    const tintMask = assetKey === 'tintMaskMediaId';
    setBusy(true);
    setMessage(tintMask
      ? 'Uploading tint mask and generating avatar renditions…'
      : 'Uploading and generating avatar renditions…');
    try {
      const mediaId = await AdminAPI.handleMediaUpload(file, MEDIA_PROFILE);
      if (!mediaId) throw new Error('Upload failed');
      setDraft(current => ({
        ...current,
        assets: { ...(current.assets || {}), [assetKey]: mediaId },
      }));
      setMessage(tintMask
        ? 'Tint mask upload ready. Save the draft, then publish it.'
        : 'Upload ready. Save the draft, then publish it.');
    } catch (error) {
      setMessage(tintMask
        ? 'Tint mask upload failed. Use a 1086×1448 PNG or WebP aligned exactly with the fixed overlay, then try again.'
        : 'Upload failed. Validate a 1086×1448 PNG or WebP aligned to BaseBody with clean alpha and try again.');
    } finally {
      setBusy(false);
    }
  };
  const upload = event => uploadAsset(event, 'mediaId');
  const uploadTintMask = event => uploadAsset(event, 'tintMaskMediaId');

  const changeState = async (action) => {
    if (!draft?.id) return;
    if (action === 'reopen') {
      const warning = draft.state === 'published'
        ? 'This published part will temporarily disappear from new selections and may fall back on existing avatars until you publish it again. Reopen it as a draft?'
        : 'This retired part will temporarily stop rendering on existing avatars until you publish it again. Reopen it as a draft?';
      if (!window.confirm(warning)) return;
    }
    setBusy(true);
    setMessage('');
    try {
      const actions = {
        publish: publishAvatarPart,
        retire: retireAvatarPart,
        reopen: reopenAvatarPart,
        reactivate: reactivateAvatarPart,
      };
      const changed = await actions[action](draft.id);
      await load(changed.id);
      const messages = {
        publish: 'Part published in a new configuration revision.',
        retire: 'Part retired for new selections.',
        reopen: 'Part reopened as a draft. You can now edit it or replace its artwork.',
        reactivate: 'Retired part reactivated and available for new selections.',
      };
      setMessage(messages[action]);
    } catch (error) {
      const errors = {
        publish: draft.renderMode === AVATAR_TINT_MASK_RENDER_MODE
          ? 'Publish failed. Save matching fixed-overlay and tint-mask uploads, a published palette, and an enabled default swatch, then try again.'
          : 'Publish failed. Save a valid 1086×1448 RGBA layer and complete metadata, then try again.',
        retire: 'This part cannot be retired.',
        reopen: 'This part cannot be reopened as a draft.',
        reactivate: 'Reactivation failed. Verify that the saved avatar artwork is still valid.',
      };
      setMessage(errors[action]);
    } finally {
      setBusy(false);
    }
  };

  const preview = useMemo(() => {
    if (!config) return null;
    const previewParts = Object.values(previewPartsBySlot);
    const previewIds = new Set(previewParts.map(part => part.id));
    const selection = fallbackSelection(config);
    previewParts.forEach(part => {
      selection.slots[part.slotKey] = part.id;
      if (part.renderMode === AVATAR_TINT_MASK_RENDER_MODE && part.defaultSwatchId) {
        selection.swatches[part.id] = part.defaultSwatchId;
      }
    });
    return {
      config: { ...config, parts: [...config.parts.filter(part => !previewIds.has(part.id)), ...previewParts] },
      selection,
    };
  }, [config, previewPartsBySlot]);
  const imagePrompt = useMemo(() => buildAvatarPartPrompt(draft), [draft]);
  const previewedPartIds = useMemo(() => new Set(
    Object.values(previewPartsBySlot).map(part => part.id),
  ), [previewPartsBySlot]);
  const layerCounts = useMemo(() => (config?.parts || []).reduce((counts, part) => {
    counts[part.slotKey] = (counts[part.slotKey] || 0) + 1;
    return counts;
  }, {}), [config]);
  const layerFilteredParts = useMemo(() => (config?.parts || []).filter(part =>
    !layerFilter || part.slotKey === layerFilter), [config, layerFilter]);
  const previewFilteredParts = useMemo(() => layerFilteredParts.filter(part =>
    !showOnlyPreviewed || previewedPartIds.has(part.id)),
  [layerFilteredParts, previewedPartIds, showOnlyPreviewed]);
  const stateCounts = useMemo(() => previewFilteredParts.reduce((counts, part) => {
    counts.all += 1;
    if (Object.prototype.hasOwnProperty.call(counts, part.state)) counts[part.state] += 1;
    return counts;
  }, { all: 0, draft: 0, published: 0, retired: 0 }), [previewFilteredParts]);
  const filteredParts = useMemo(() => previewFilteredParts.filter(part =>
    !stateFilter || part.state === stateFilter), [previewFilteredParts, stateFilter]);
  const bulkSelectedParts = useMemo(() => (config?.parts || []).filter(part =>
    bulkSelectedIds.has(part.id)), [bulkSelectedIds, config]);
  const bulkEligibleParts = useMemo(() => bulkSelectedParts.filter(part =>
    canBulkChangePart(part, bulkAction)), [bulkAction, bulkSelectedParts]);
  const selectedShownCount = useMemo(() => filteredParts.filter(part =>
    bulkSelectedIds.has(part.id)).length, [bulkSelectedIds, filteredParts]);
  const allShownSelected = filteredParts.length > 0 && selectedShownCount === filteredParts.length;
  useEffect(() => {
    if (bulkSelectAllRef.current) {
      bulkSelectAllRef.current.indeterminate = selectedShownCount > 0 && !allShownSelected;
    }
  }, [allShownSelected, bulkOpen, selectedShownCount]);
  const isEditableDraft = draft?.state === 'draft';
  const tintMaskMode = draft?.renderMode === AVATAR_TINT_MASK_RENDER_MODE;
  const publishedPalettes = getPublishedPalettes(config);
  const defaultSwatches = tintMaskMode ? getEnabledPaletteSwatches(draft, config) : [];
  const selectedLayerSlot = config?.layerSlots?.find(slot => slot.key === draft?.slotKey);
  const previewPartCount = Object.keys(previewPartsBySlot).length;
  const currentPreviewPart = () => ({ ...payload(), id: draft.id || '__preview_unsaved', state: 'draft' });

  const toggleBulkPart = (partId, selected) => {
    setBulkSelectedIds(current => {
      const next = new Set(current);
      if (selected) next.add(partId);
      else next.delete(partId);
      return next;
    });
    setBulkMessage('');
  };

  const toggleAllShownParts = (selected) => {
    setBulkSelectedIds(current => {
      const next = new Set(current);
      filteredParts.forEach(part => {
        if (selected) next.add(part.id);
        else next.delete(part.id);
      });
      return next;
    });
    setBulkMessage('');
  };

  const clearBulkSelection = () => {
    setBulkSelectedIds(new Set());
    setBulkAction('');
    setBulkMessage('');
  };

  const applyBulkAction = async () => {
    const definition = BULK_PART_ACTIONS[bulkAction];
    if (!definition || !bulkEligibleParts.length) return;

    const skippedCount = bulkSelectedParts.length - bulkEligibleParts.length;
    const countLabel = `${bulkEligibleParts.length} ${bulkEligibleParts.length === 1 ? 'part' : 'parts'}`;
    const consequence = bulkAction === 'reopen'
      ? ' These parts will become draft-only and stop being available until they are published again.'
      : '';
    const skipped = skippedCount
      ? ` ${skippedCount} ineligible selected ${skippedCount === 1 ? 'part will' : 'parts will'} be skipped.`
      : '';
    if (!window.confirm(`${definition.label} for ${countLabel}?${consequence}${skipped}`)) return;

    setBusy(true);
    setMessage('');
    setBulkMessage(`${definition.label}…`);
    const failedIds = new Set();
    for (const part of bulkEligibleParts) {
      try {
        await definition.run(part.id);
      } catch (error) {
        failedIds.add(part.id);
      }
    }

    const successfulIds = new Set(bulkEligibleParts
      .filter(part => !failedIds.has(part.id))
      .map(part => part.id));
    try {
      await load(selectedId);
      setBulkSelectedIds(current => new Set([...current].filter(id => !successfulIds.has(id))));
      const succeededCount = successfulIds.size;
      const remainingCount = skippedCount + failedIds.size;
      const successText = succeededCount
        ? `${definition.successVerb} ${succeededCount} ${succeededCount === 1 ? 'part' : 'parts'}.`
        : `No parts were ${definition.successVerb.toLowerCase()}.`;
      setBulkMessage(remainingCount
        ? `${successText} ${remainingCount} selected ${remainingCount === 1 ? 'part remains' : 'parts remain'} for review.`
        : `${successText} Selection cleared.`);
    } catch (error) {
      setBulkMessage('The bulk changes were sent, but the updated part list could not be loaded. Refresh the Workshop before continuing.');
    } finally {
      setBusy(false);
    }
  };

  const previewPart = () => {
    const part = currentPreviewPart();
    const previousSlot = Object.values(previewPartsBySlot).find(item => item.id === part.id)?.slotKey;
    const replacing = !!previewPartsBySlot[part.slotKey];
    setPreviewPartsBySlot(current => {
      const next = Object.fromEntries(Object.entries(current)
        .filter(([, item]) => item.id !== part.id));
      next[part.slotKey] = part;
      return next;
    });
    setMessage(previousSlot && previousSlot !== part.slotKey
      ? `${part.name || 'Part'} moved from ${previousSlot} to ${part.slotKey} in the combined preview.`
      : replacing
      ? `${part.name || 'Part'} replaced the ${part.slotKey} layer in the combined preview.`
      : `${part.name || 'Part'} added to the combined preview.`);
  };

  const randomizePreview = () => {
    const selectedPart = currentPreviewPart();
    const previewPool = [
      ...config.parts.filter(part => part.id !== selectedPart.id && part.slotKey !== selectedPart.slotKey),
      selectedPart,
    ];
    const randomConfig = {
      ...config,
      parts: previewPool.map(part => ({
        ...part,
        state: 'published',
        starterEligible: true,
        templateKey: part.templateKey || config.templateKey,
        requiresTags: [],
        excludesTags: [],
      })),
    };
    const randomized = randomizeSelection(randomConfig);
    const partsById = new Map(previewPool.map(part => [part.id, part]));
    const next = Object.fromEntries(Object.entries(randomized.slots).flatMap(([slotKey, partId]) => {
      const part = partsById.get(partId);
      return part?.slotKey === slotKey ? [[slotKey, part]] : [];
    }));
    next[selectedPart.slotKey] = selectedPart;
    setPreviewPartsBySlot(next);
    setMessage(`Randomized preview with ${Object.keys(next).length} equipped slots; optional layers may be empty. ${selectedPart.name || 'The selected part'} is equipped.`);
  };

  const resetPreview = () => {
    setPreviewPartsBySlot({});
    setMessage('Combined preview reset to the base avatar.');
  };

  const changeLayerRequirement = async (required) => {
    setBusy(true);
    setMessage('');
    try {
      const nextConfig = await saveAvatarLayerRequirement(draft.slotKey, required);
      if (!nextConfig) throw new Error('Layer requirement update failed');
      setConfig(nextConfig);
      const slot = nextConfig.layerSlots.find(item => item.key === draft.slotKey);
      setMessage(required
        ? `${slot?.name || draft.slotKey} now always requires an equipped part.`
        : `${slot?.name || draft.slotKey} is now optional and may be empty when randomized.`);
    } catch (error) {
      setMessage('Could not update this layer rule. Required layers need at least one published part.');
    } finally {
      setBusy(false);
    }
  };

  const copyPrompt = async () => {
    setCopyMessage('');
    try {
      await navigator.clipboard.writeText(imagePrompt);
      setCopyMessage(draft.slotKey === 'body_base'
        ? `BaseBodyV3 replacement prompt copied. In a fresh image conversation attach ${PROPORTION_GUIDE}, V2 BaseBody.png, then ${ART_DIRECTION_REFERENCE}.`
        : `${BASE_BODY_REFERENCE}-registered part prompt copied.`);
    } catch (error) {
      setCopyMessage('Copy failed. Select the prompt and copy it manually.');
    }
  };

  if (!config || !draft) return <div className="admin-operatives"><p>Loading Operative Workshop…</p></div>;

  return (
    <main className="admin-operatives">
      {tutorialOpen && <TutorialModal onClose={() => setTutorialOpen(false)} />}

      <div className="admin-operatives__layout">
        <aside className="admin-operatives__library" aria-label="Avatar part library">
          <div className="admin-operatives__library-actions">
            <button className="button-primary" onClick={() => { setSelectedId(''); setDraft(draftFor(config)); setMessage(''); setCopyMessage(''); }} type="button">New Part</button>
            <button className="button-secondary" onClick={() => setTutorialOpen(true)} type="button">Tutorial</button>
          </div>
          <div className="admin-operatives__library-filters">
            <label>Layer<select onChange={event => setLayerFilter(event.target.value)} value={layerFilter}>
              <option value="">All layers ({config.parts.length})</option>
              {config.layerSlots.map(slot => <option key={slot.key} value={slot.key}>{slot.name} ({layerCounts[slot.key] || 0})</option>)}
            </select></label>
            <label>Status<select onChange={event => setStateFilter(event.target.value)} value={stateFilter}>
              <option value="">All ({stateCounts.all})</option>
              <option value="draft">Draft ({stateCounts.draft})</option>
              <option value="published">Released ({stateCounts.published})</option>
              <option value="retired">Retired ({stateCounts.retired})</option>
            </select></label>
            <label className="admin-operatives__preview-filter">
              <input checked={showOnlyPreviewed} onChange={event => setShowOnlyPreviewed(event.target.checked)} type="checkbox" />
              Show only previewed
            </label>
          </div>
          <section aria-label="Bulk part actions" className="admin-operatives__bulk" aria-busy={busy}>
            <button
              aria-controls="admin-operatives-bulk-controls"
              aria-expanded={bulkOpen}
              aria-label={`${bulkOpen ? 'Hide' : 'Show'} bulk edit controls${bulkSelectedParts.length ? `, ${bulkSelectedParts.length} selected` : ''}`}
              className="admin-operatives__bulk-toggle"
              disabled={busy}
              onClick={() => setBulkOpen(current => !current)}
              type="button"
            >
              <strong>Bulk edit</strong>
              <span>{bulkSelectedParts.length ? `${bulkSelectedParts.length} selected` : bulkOpen ? 'Hide' : 'Show'} <span aria-hidden="true">{bulkOpen ? '▴' : '▾'}</span></span>
            </button>
            {bulkOpen && <div className="admin-operatives__bulk-controls" id="admin-operatives-bulk-controls">
              <div className="admin-operatives__bulk-summary">
                <label>
                  <input
                    checked={allShownSelected}
                    disabled={!filteredParts.length || busy}
                    onChange={event => toggleAllShownParts(event.target.checked)}
                    ref={bulkSelectAllRef}
                    type="checkbox"
                  />
                  Select shown
                </label>
                <span aria-live="polite">{bulkSelectedParts.length} selected</span>
              </div>
              <label>Bulk action<select disabled={!bulkSelectedParts.length || busy} onChange={event => { setBulkAction(event.target.value); setBulkMessage(''); }} value={bulkAction}>
                <option value="">Choose action…</option>
                {Object.entries(BULK_PART_ACTIONS).map(([value, action]) => (
                  <option key={value} value={value}>{action.label}</option>
                ))}
              </select></label>
              <div className="admin-operatives__bulk-actions">
                <button
                  className="button-primary"
                  disabled={busy || !bulkEligibleParts.length}
                  onClick={applyBulkAction}
                  type="button"
                >Apply{bulkAction ? ` (${bulkEligibleParts.length})` : ''}</button>
                <button className="button-secondary" disabled={busy || !bulkSelectedParts.length} onClick={clearBulkSelection} type="button">Clear</button>
              </div>
              {bulkAction && bulkSelectedParts.length > bulkEligibleParts.length && (
                <small>{bulkSelectedParts.length - bulkEligibleParts.length} selected {bulkSelectedParts.length - bulkEligibleParts.length === 1 ? 'part is' : 'parts are'} not eligible for this action.</small>
              )}
              {bulkMessage && <p aria-live="polite" className="admin-operatives__bulk-message">{bulkMessage}</p>}
            </div>}
          </section>
          <div aria-label="Body part list" className="admin-operatives__library-results" role="region" tabIndex="0">
            {filteredParts.map(part => (
              <div className={[
                'admin-operatives__library-result',
                bulkOpen && 'is-bulk-open',
                bulkOpen && bulkSelectedIds.has(part.id) && 'is-bulk-selected',
              ].filter(Boolean).join(' ')} key={part.id}>
                {bulkOpen && <label className="admin-operatives__part-selector">
                  <input
                    aria-label={`Select ${part.name}`}
                    checked={bulkSelectedIds.has(part.id)}
                    disabled={busy}
                    onChange={event => toggleBulkPart(part.id, event.target.checked)}
                    type="checkbox"
                  />
                </label>}
                <button
                  aria-pressed={selectedId === part.id}
                  className={[
                    selectedId === part.id && 'is-selected',
                    previewedPartIds.has(part.id) && 'is-previewed',
                  ].filter(Boolean).join(' ')}
                  onClick={() => { setSelectedId(part.id); setDraft(draftFor(config, part)); setMessage(''); setCopyMessage(''); }}
                  type="button"
                >
                  <strong>{part.name}</strong>
                  <span>{part.slotKey} · {part.state}</span>
                  {previewedPartIds.has(part.id) && <span className="admin-operatives__previewed-status">Previewed</span>}
                </button>
              </div>
            ))}
            {!filteredParts.length && <p className="admin-operatives__library-empty" role="status">No parts match these filters.</p>}
          </div>
        </aside>

        <form className="admin-operatives__inspector" onSubmit={(event) => { event.preventDefault(); save(); }}>
          <h3>{draft.id ? 'Part Inspector' : 'New Avatar Part'}</h3>
          {draft.id && !isEditableDraft && (
            <p className="admin-operatives__warning" role="status">
              <strong>{draft.state === 'published' ? 'Published part.' : 'Retired part.'}</strong>{' '}
              {draft.builtInFallback
                ? 'The built-in fallback is protected.'
                : 'Reopen it as a draft to edit metadata or replace the uploaded artwork.'}
            </p>
          )}
          <label>Name<input disabled={!isEditableDraft} maxLength="100" onChange={event => update('name', event.target.value)} required value={draft.name} /></label>
          <label>Slug<input disabled={!isEditableDraft} onChange={event => update('slug', event.target.value)} pattern="[a-z0-9]+(?:-[a-z0-9]+)*" placeholder="sand-squint" required value={draft.slug} /></label>
          <div className="admin-operatives__description-preview">
            <div className="admin-operatives__description">
              <label>Visual description / flavor text<textarea disabled={!isEditableDraft} onChange={event => update('flavorText', event.target.value)} placeholder="Describe the shape, expression, materials, colors, and wear for the prompt." value={draft.flavorText || ''} /></label>
              <UploadedLayerPreview config={config} part={draft} />
            </div>
            <section className="admin-operatives__preview" aria-label="Combined avatar preview">
              <h4>Combined Preview</h4>
              <div aria-label="Preview size" className="admin-operatives__preview-modes" role="group">
                <button aria-pressed={previewMode === 'full'} onClick={() => setPreviewMode('full')} type="button">Full size</button>
                <button aria-pressed={previewMode === 'map'} onClick={() => setPreviewMode('map')} type="button">Map size</button>
              </div>
              {preview && (
                <div className={`admin-operatives__preview-canvas admin-operatives__preview-canvas--${previewMode}`}>
                  <AvatarComposite
                    config={preview.config}
                    includeDrafts
                    label={`Combined avatar ${previewMode}-size preview`}
                    renderProfile={previewMode === 'map' ? AVATAR_PROFILES.MAP_SELECTED : AVATAR_PROFILES.PORTRAIT}
                    selection={preview.selection}
                  />
                </div>
              )}
              <p aria-live="polite">{previewPartCount ? `${previewPartCount} previewed ${previewPartCount === 1 ? 'slot' : 'slots'}` : 'Base avatar only'} · {config.templateKey}</p>
              <div className="admin-operatives__preview-actions">
                <button className="button-primary" onClick={previewPart} type="button">Preview Part</button>
                <button className="button-secondary" onClick={randomizePreview} type="button">Randomize Preview</button>
                <button className="button-secondary" disabled={!previewPartCount} onClick={resetPreview} type="button">Reset Preview</button>
              </div>
            </section>
          </div>
          <label>Category<select disabled={!isEditableDraft} onChange={event => updateCategory(event.target.value)} value={draft.categoryId}>{config.categories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}</select><small>Controls where players find the part.</small></label>
          <label>Layer slot<select disabled={!isEditableDraft} onChange={event => update('slotKey', event.target.value)} value={draft.slotKey}>{config.layerSlots.map(slot => <option key={slot.key} value={slot.key}>{slot.name}</option>)}</select><small>Controls its exact stack position. Eyes/irises and shirts/jackets are separate layers.</small></label>
          <label>Layer requirement<select disabled={busy} onChange={event => changeLayerRequirement(event.target.value === 'required')} value={selectedLayerSlot?.required ? 'required' : 'optional'}>
            <option value="required">Required — always equip one</option>
            <option value="optional">Optional — allow None</option>
          </select><small>Global rule for every part in this layer. Saved immediately; randomization can leave optional layers empty.</small></label>
          <label>Render mode<select disabled={!isEditableDraft} onChange={event => changeRenderMode(event.target.value)} value={draft.renderMode}>
            <option value={RGBA_RENDER_MODE}>RGBA — fixed colors</option>
            <option value={AVATAR_TINT_MASK_RENDER_MODE}>Tint mask — palette color</option>
          </select><small>RGBA uses one complete layer. Tint mask combines a colorable skin mask with a fixed ink and underwear overlay.</small></label>
          {tintMaskMode && <fieldset className="admin-operatives__tint-settings" disabled={!isEditableDraft}>
            <legend>Tint settings</legend>
            <label>Published palette<select onChange={event => changePalette(event.target.value)} value={draft.paletteId}>
              <option value="">{publishedPalettes.length ? 'Choose a published palette…' : 'No published palettes available'}</option>
              {publishedPalettes.map(palette => <option key={palette.id} value={palette.id}>{palette.name || palette.id}</option>)}
            </select><small>Only published palettes can be used by players.</small></label>
            <label>Default swatch<select disabled={!draft.paletteId || !defaultSwatches.length} onChange={event => update('defaultSwatchId', event.target.value)} value={draft.defaultSwatchId}>
              <option value="">{defaultSwatches.length ? 'Choose an enabled swatch…' : 'No enabled swatches available'}</option>
              {defaultSwatches.map(swatch => <option key={swatch.id} value={swatch.id}>{swatch.label || swatch.id} · {swatch.value}</option>)}
            </select><small>The preview and new player selections start with this named tone.</small></label>
          </fieldset>}
          <div className="admin-operatives__row">
            <label>Sort order<input disabled={!isEditableDraft} min="0" onChange={event => update('sortOrder', Number(event.target.value))} type="number" value={draft.sortOrder} /></label>
            <label>Random weight<input disabled={!isEditableDraft} min="0" onChange={event => update('randomWeight', Number(event.target.value))} type="number" value={draft.randomWeight} /></label>
          </div>
          <label className="admin-operatives__check"><input checked={draft.starterEligible} disabled={!isEditableDraft} onChange={event => update('starterEligible', event.target.checked)} type="checkbox" /> Starter/randomizer eligible</label>
          <label>Tags<input disabled={!isEditableDraft} onChange={event => update('tagsText', event.target.value)} placeholder="hair, long" value={draft.tagsText} /></label>
          <label>Requires tags<input disabled={!isEditableDraft} onChange={event => update('requiresTagsText', event.target.value)} value={draft.requiresTagsText} /></label>
          <label>Excludes tags<input disabled={!isEditableDraft} onChange={event => update('excludesTagsText', event.target.value)} value={draft.excludesTagsText} /></label>
          <label>Hides slots<input disabled={!isEditableDraft} onChange={event => update('hidesSlotsText', event.target.value)} placeholder="hair_front, hair_back" value={draft.hidesSlotsText} /></label>
          <details className="admin-operatives__prompt admin-image-prompt">
            <summary>Image Generator Prompt</summary>
            {draft.slotKey === 'body_base'
              ? <p><strong>BaseBodyV3 is the approved active body.</strong> Use this prompt only when intentionally replacing it; the current measured asset remains authoritative until a replacement is approved and recalibrated.</p>
              : <p><strong>This fitted-part prompt is active.</strong> It uses the approved BaseBodyV3 and calibrated frontal anchors, bounds, crop, and ground line.</p>}
            <div className="admin-operatives__prompt-controls">
              <button className="button-primary" onClick={copyPrompt} type="button">{draft.slotKey === 'body_base' ? 'Copy BaseBodyV3 Replacement Prompt' : 'Copy V3 Part Prompt'}</button>
              <textarea aria-label="Avatar Part Image Generator Prompt" readOnly rows="3" value={imagePrompt} />
              {copyMessage && <span role="status">{copyMessage}</span>}
            </div>
          </details>
          <div className="admin-operatives__uploads">
            <label className="admin-operatives__upload">{tintMaskMode ? 'Fixed ink / underwear overlay' : 'Validated 1086×1448 BaseBody-aligned layer'}<input accept="image/png,image/webp" disabled={busy || !isEditableDraft} onChange={upload} type="file" /><span>{!isEditableDraft ? 'Reopen as draft to replace this artwork' : draft.assets?.mediaId ? `Media ${draft.assets.mediaId} · upload another file to replace it` : tintMaskMode ? 'No overlay uploaded · outlines, shading, and opaque underwear · skin areas transparent' : 'No layer uploaded · PNG/WebP · clean straight alpha · native 3:4 registration'}</span></label>
            {tintMaskMode && <label className="admin-operatives__upload">Tint mask (skin fill)<input accept="image/png,image/webp" disabled={busy || !isEditableDraft} onChange={uploadTintMask} type="file" /><span>{!isEditableDraft ? 'Reopen as draft to replace this tint mask' : draft.assets?.tintMaskMediaId ? `Media ${draft.assets.tintMaskMediaId} · upload another file to replace it` : 'No tint mask uploaded · same 1086×1448 registration · opaque only where skin receives color'}</span></label>}
          </div>
          <div className="admin-operatives__actions">
            <button className="button-primary" disabled={busy || !isEditableDraft} type="submit">{busy ? 'Working…' : 'Save Draft'}</button>
            <button className="button-secondary" disabled={busy || !draft.id || !isEditableDraft} onClick={() => changeState('publish')} type="button">Publish</button>
            <button className="button-secondary" disabled={busy || !draft.id || isEditableDraft || draft.builtInFallback} onClick={() => changeState('reopen')} type="button">Reopen as Draft</button>
            <button className="button-secondary" disabled={busy || draft.state !== 'published' || draft.builtInFallback} onClick={() => changeState('retire')} type="button">Retire</button>
            <button className="button-secondary" disabled={busy || draft.state !== 'retired'} onClick={() => changeState('reactivate')} type="button">Reactivate</button>
          </div>
          {message && <p aria-live="polite" className="admin-operatives__message">{message}</p>}
        </form>
      </div>
    </main>
  );
}
